import axios, { AxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { assert } from '@/utils'
import { CHAIN_CONFIG, CHAIN_INFO_LIST, CHAIN_SUPPORTS_LSM, STRIDE_CHAIN_INFO } from '@/config'
import { queryKeys } from '@/query-keys'
import { z } from 'zod'
import { useSelectedChain, useStrideChain, useStrideChainBalance } from '@/contexts/wallet'
import { useSelectedDenom } from '@/contexts/denom'

export interface LsmIbcBalance {
  amount: string
  ibcDenom: string
  tokenizedDenom: string
}

export interface LsmIbcBalancesQueryPayload {
  lsmIbcBalances: LsmIbcBalance[]
}

// This was originally part of StakeTransactionHistory / useTransactionHistory, but ultimately
// decided to make it separate. This is to guarantee that LSM continue flow will always work.
// At this time, edge proxy is not able to handle the tx endpoint for osme addresses, and ends up
// responding with an error code 413 (more on this in Transaction History). In addition, this
// makes lsm ibc transactions to be placed on top of the list for visibility.
//
// There is a known bug where LsmIbc transactions are often not removed despite being invalidated
// when the revert flow succeeds. This is likely because there are stale values, and I have no idea
// how to fix it.
const useLsmIbcBalancesQuery = () => {
  const { address: strideAddress } = useStrideChain()

  const { address: selectedAddress } = useSelectedChain()

  const [denom] = useSelectedDenom()

  // @TODO: Handle errors properly - for now StakeWallet uses this query extensively and has implemented some errors states
  const { data: strideChainBalances } = useStrideChainBalance()

  // Unlike most of the transaction history, LsmIbc does not depend on the IBC messages we usually query through
  // the /txs endpoint. LsmIbc uses the stride account's balances and does some querying to an entirely different
  // endpoint to check if the ibc denom is actually an lsm balance. In a sense, it also does not use `useIbcStatusQuery`
  // and is not dismissible - we want users to have complete visibility over their LSM transactions, and push them to either
  // liquid stake them or revert them back as natively staked balance.
  const handleQueryLsmIbcBalances = async (): Promise<LsmIbcBalancesQueryPayload> => {
    assert(strideChainBalances, 'Unable to query lsm ibc balances while balances are empty.')

    if (!CHAIN_SUPPORTS_LSM[denom]) {
      return { lsmIbcBalances: [] }
    }

    const lsmIbcBalances = await Promise.all(
      (strideChainBalances.balances ?? []).map(async (coin) => {
        if (!coin.denom.startsWith('ibc')) {
          return null
        }

        const ibcDenomHash = coin.denom.replace('ibc/', '')

        try {
          const { base, trace } = (await getIbcDenom(ibcDenomHash)).denom

          const chainInfo = CHAIN_INFO_LIST[denom]

          const chainConfig = CHAIN_CONFIG[denom]

          // Guarantee that the ibc denom hash is for the selected chain.
          // LSM tokenized shares are tokenized on the source chain and IBC'd
          // directly to Stride, so they're always single-hop.
          const isSingleHopFromSelectedChain =
            trace.length === 1 && trace[0].port_id === 'transfer' && trace[0].channel_id === chainConfig.withdrawChannel

          if (!isSingleHopFromSelectedChain || !base.startsWith(chainInfo.bech32Config.bech32PrefixValAddr)) {
            return null
          }

          // We dont necessarily need the return value of ibc denom hash - if it fails,
          // it's likely that it's not an ibc denom hash for an lsm balance.
          return {
            amount: coin.amount,
            ibcDenom: coin.denom,
            tokenizedDenom: base
          }
        } catch (e: unknown) {
          if (e instanceof AxiosError && e.response?.status === 404) {
            return null
          }

          // If it's not a "Not Found" error, it's likely a legitimiate error.
          throw e
        }
      })
    )

    return {
      lsmIbcBalances: lsmIbcBalances.filter((balance): balance is NonNullable<LsmIbcBalance> => {
        return balance != null
      })
    }
  }

  const params = {
    strideAccount: strideAddress ?? '',
    selectedAccount: selectedAddress ?? '',
    signature: strideChainBalances?.signature ?? ''
  }

  return useQuery({
    queryKey: queryKeys.transactionHistoryLsmIbcBalancesByAddress(params),
    queryFn: handleQueryLsmIbcBalances,
    enabled: Boolean(strideAddress && selectedAddress && strideChainBalances)
  })
}

// The /denoms/{hash} endpoint replaced /denom_traces/{hash} in ibc-go v10
// (shipped to Stride mainnet in the v32 upgrade). The old path now returns
// HTTP 501 for every request.
const IbcTransferDenomSchema = z.object({
  denom: z.object({
    base: z.string(),
    trace: z.array(
      z.object({
        port_id: z.string(),
        channel_id: z.string()
      })
    )
  })
})

const getIbcDenom = async (ibcDenomHash: string) => {
  const instance = axios.create({
    baseURL: STRIDE_CHAIN_INFO.rest
  })

  const response = await instance.get(`ibc/apps/transfer/v1/denoms/${ibcDenomHash}`)

  return IbcTransferDenomSchema.parse(response.data)
}

export { useLsmIbcBalancesQuery }
